import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookBookmark, CheckCircle, ListChecks, Detective, UsersThree, GraduationCap, Brain, CheckFat, WarningCircle, MagnifyingGlass, Spinner } from "@phosphor-icons/react";
import { BloomBadge } from "@/components/styleguide/ui/BloomBadge";
import { api } from "../api";

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-5 shadow-soft">
      <span className={`flex h-10 w-10 items-center justify-center rounded-2xl ${tint}`}>
        <Icon size={19} weight="bold" />
      </span>
      <p className="mt-3 font-display text-3xl font-black text-foreground">{value ?? "…"}</p>
      <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

function QuizBankGenerator() {
  const [words, setWords]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [results, setResults]   = useState(null);
  const textareaRef             = useRef(null);

  async function handleGenerate() {
    const trimmed = words.trim();
    if (!trimmed) return;
    setLoading(true);
    setResults(null);
    try {
      const data = await api("/admin/quiz-bank/generate", {
        method: "POST",
        body: { words: trimmed },
      });
      setResults(data.results);
    } catch (err) {
      setResults([{ word: "—", status: "error", detail: err?.message ?? "Request failed" }]);
    } finally {
      setLoading(false);
    }
  }

  const wordCount = words.split(",").filter((w) => w.trim()).length;

  return (
    <div className="mt-6 rounded-3xl border border-border bg-card p-5 shadow-soft">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/12 text-primary-strong">
          <Brain size={18} weight="bold" />
        </span>
        <div>
          <p className="font-display text-base font-black text-foreground">Quiz Bank Generator</p>
          <p className="text-xs font-semibold text-muted-foreground">Generate 12 AI questions per word (meaning, sentence, context, odd one out)</p>
        </div>
      </div>

      <div className="mt-4">
        <textarea
          ref={textareaRef}
          value={words}
          onChange={(e) => setWords(e.target.value)}
          placeholder="apex, bewildered, candid, diligent, eloquent…"
          rows={3}
          className="w-full resize-none rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm font-semibold text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs font-semibold text-muted-foreground">
            {wordCount > 0 ? `${wordCount} word${wordCount !== 1 ? "s" : ""} · ~${wordCount * 12} questions` : "Comma-separate multiple words"}
          </p>
          <button
            onClick={handleGenerate}
            disabled={loading || !words.trim()}
            className="flex items-center gap-2 rounded-2xl bg-primary px-5 py-2 text-sm font-extrabold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <>
                <Spinner size={15} className="animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Brain size={15} weight="bold" />
                Generate
              </>
            )}
          </button>
        </div>
      </div>

      {results && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Results</p>
          {results.map((r, i) => (
            <div
              key={i}
              className={`flex items-center justify-between rounded-2xl px-4 py-3 ${
                r.status === "ok"        ? "bg-success/10" :
                r.status === "not_found" ? "bg-muted/50"   :
                                           "bg-destructive/10"
              }`}
            >
              <div className="flex items-center gap-2">
                {r.status === "ok"        && <CheckFat    size={16} weight="fill" className="shrink-0 text-success-ink" />}
                {r.status === "not_found" && <MagnifyingGlass size={16} weight="bold" className="shrink-0 text-muted-foreground" />}
                {r.status === "error"     && <WarningCircle   size={16} weight="fill" className="shrink-0 text-destructive" />}
                <span className="font-display text-sm font-extrabold capitalize text-foreground">{r.word}</span>
                {r.status === "error" && r.detail && (
                  <span className="text-xs font-semibold text-destructive/80">{r.detail}</span>
                )}
              </div>
              <span className="flex gap-2">
                {r.status === "ok" && (
                  <>
                    <BloomBadge variant="success" className="px-2.5 py-0.5 text-xs">{r.questions_count} questions</BloomBadge>
                    <BloomBadge variant="primary" className="px-2.5 py-0.5 text-xs">{r.model}</BloomBadge>
                  </>
                )}
                {r.status === "not_found" && (
                  <BloomBadge variant="default" className="px-2.5 py-0.5 text-xs">Not in vocab</BloomBadge>
                )}
                {r.status === "error" && (
                  <BloomBadge variant="destructive" className="px-2.5 py-0.5 text-xs">Failed</BloomBadge>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api("/admin/stats").then(setStats).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-5xl" data-testid="admin-dashboard">
      <h1 className="font-display text-3xl font-black tracking-tight text-foreground">Dashboard</h1>
      <p className="mt-1 text-sm font-semibold text-muted-foreground">Bloom's vocabulary at a glance.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard icon={BookBookmark} label="Total words" value={stats?.words_total} tint="bg-primary/12 text-primary-strong" />
        <StatCard icon={CheckCircle} label="Approved (live)" value={stats?.words.approved} tint="bg-success/15 text-success-ink" />
        <StatCard icon={ListChecks} label="Awaiting review" value={stats ? stats.words.draft + stats.words.review : null} tint="bg-warning/15 text-warning-ink" />
        <StatCard icon={Detective} label="Missing words" value={stats?.missing_open} tint="bg-blue/12 text-blue-ink" />
        <StatCard icon={UsersThree} label="Parents" value={stats?.parents} tint="bg-teal/15 text-teal-ink" />
        <StatCard icon={GraduationCap} label="Children" value={stats?.children} tint="bg-secondary/25 text-secondary-ink" />
      </div>

      <div className="mt-6 rounded-3xl border border-border bg-card p-5 shadow-soft">
        <div className="flex items-center justify-between">
          <p className="font-display text-base font-black text-foreground">Top missing words</p>
          <Link to="/admin/missing" className="text-sm font-extrabold text-primary-strong hover:underline" data-testid="see-all-missing">See all</Link>
        </div>
        {stats?.top_missing?.length === 0 && <p className="mt-3 text-sm font-semibold text-muted-foreground">No missing words yet — children haven't hit any gaps.</p>}
        <div className="mt-3 space-y-2">
          {stats?.top_missing?.map((m) => (
            <div key={m.missing_id} className="flex items-center justify-between rounded-2xl bg-muted/50 px-4 py-3">
              <span className="font-display text-base font-extrabold capitalize text-foreground">{m.word_display}</span>
              <span className="flex gap-2">
                <BloomBadge variant="primary" className="px-2.5 py-0.5 text-xs">{m.total_requests} requests</BloomBadge>
                <BloomBadge variant="teal" className="px-2.5 py-0.5 text-xs">{m.unique_children} children</BloomBadge>
              </span>
            </div>
          ))}
        </div>
      </div>

      <QuizBankGenerator />
    </div>
  );
}
